import { randomUUID } from "node:crypto";
import path from "node:path";
import { GatewayClient } from "@mewbleh/nori-gateway";

type RpcRequest = { jsonrpc: "2.0"; id?: string | number; method: string; params?: Record<string, unknown> };
type AcpSession = { sessionId: string; sessionKey: string; cwd: string };

function absoluteCwd(value: unknown): string {
  const cwd = String(value ?? process.cwd());
  if (!path.isAbsolute(cwd)) throw Object.assign(new Error("ACP session cwd must be absolute"), { code: -32602 });
  return cwd;
}

function rejectMcpServers(params: Record<string, unknown>): void {
  if (Array.isArray(params.mcpServers) && params.mcpServers.length) throw Object.assign(new Error("Per-session MCP servers are unsupported; configure mcp.servers on Nori instead"), { code: -32602 });
}

function promptContent(value: unknown): { text: string; media: Array<Record<string, unknown>> } {
  if (!Array.isArray(value)) throw Object.assign(new Error("prompt must be an array of content blocks"), { code: -32602 });
  const text: string[] = [];
  const media: Array<Record<string, unknown>> = [];
  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const part = item as Record<string, unknown>;
    if (part.type === "text" && typeof part.text === "string") text.push(part.text);
    else if (part.type === "resource" && part.resource && typeof part.resource === "object") {
      const resource = part.resource as Record<string, unknown>;
      if (typeof resource.text === "string") text.push(resource.text);
      else text.push(`[Resource: ${String(resource.uri ?? "embedded")}]`);
    } else if (part.type === "image") {
      media.push({ mediaType: String(part.mimeType ?? "image/png"), ...(part.data ? { data: String(part.data) } : {}), ...(part.uri ? { url: String(part.uri) } : {}) });
    }
  }
  const joined = text.join("\n\n").trim();
  if (Buffer.byteLength(joined) > 1024 * 1024) throw Object.assign(new Error("ACP prompt exceeds 1 MiB"), { code: -32602 });
  return { text: joined, media };
}

export class NoriAcpBridge {
  readonly sessions = new Map<string, AcpSession>();

  constructor(
    readonly client: GatewayClient,
    readonly notify: (method: string, params: unknown) => void,
    readonly options: { sessionKey?: string; prefixCwd?: boolean } = {},
  ) {}

  #session(id: unknown): AcpSession {
    const session = this.sessions.get(String(id ?? ""));
    if (!session) throw Object.assign(new Error(`ACP session not found: ${String(id ?? "")}`), { code: -32602 });
    return session;
  }

  async handle(request: RpcRequest): Promise<unknown> {
    const params = request.params ?? {};
    switch (request.method) {
      case "initialize":
        return {
          protocolVersion: 1,
          agentCapabilities: {
            loadSession: true,
            promptCapabilities: { image: true, audio: false, embeddedContext: true },
            mcpCapabilities: { http: false, sse: false },
            sessionCapabilities: { list: {}, resume: {}, close: {} },
          },
          agentInfo: { name: "nori-acp", title: "Nori ACP Gateway", version: "0.3.0" },
          authMethods: [],
        };
      case "authenticate":
        return {};
      case "session/new": {
        rejectMcpServers(params);
        const sessionId = randomUUID();
        const session: AcpSession = {
          sessionId,
          sessionKey: this.options.sessionKey ?? `acp-bridge:${sessionId}`,
          cwd: absoluteCwd(params.cwd),
        };
        this.sessions.set(sessionId, session);
        return { sessionId };
      }
      case "session/load": {
        rejectMcpServers(params);
        const sessionId = String(params.sessionId ?? "");
        const session = { sessionId, sessionKey: this.options.sessionKey ?? sessionId, cwd: absoluteCwd(params.cwd) };
        const result = await this.client.request("sessions.get", { key: session.sessionKey, limit: 200 }) as { messages?: Array<Record<string, unknown>> };
        this.sessions.set(sessionId, session);
        for (const message of result.messages ?? []) {
          if (message.role !== "user" && message.role !== "assistant") continue;
          const content = typeof message.content === "string" ? message.content : JSON.stringify(message.content);
          this.notify("session/update", { sessionId, update: { sessionUpdate: message.role === "assistant" ? "agent_message_chunk" : "user_message_chunk", content: { type: "text", text: content } } });
        }
        return {};
      }
      case "session/resume": {
        rejectMcpServers(params);
        const sessionId = String(params.sessionId ?? "");
        await this.client.request("sessions.describe", { key: this.options.sessionKey ?? sessionId });
        this.sessions.set(sessionId, { sessionId, sessionKey: this.options.sessionKey ?? sessionId, cwd: absoluteCwd(params.cwd) });
        return {};
      }
      case "session/list": {
        const result = await this.client.request("sessions.list", { limit: 100, offset: params.cursor ? Number(params.cursor) : 0 }) as { sessions?: Array<Record<string, unknown>> };
        return {
          sessions: (result.sessions ?? []).map((session) => ({
            sessionId: String(session.sessionKey),
            cwd: absoluteCwd(params.cwd),
            title: String(session.label ?? session.sessionKey),
            updatedAt: new Date(Number(session.updatedAt ?? Date.now())).toISOString(),
          })),
          nextCursor: null,
        };
      }
      case "session/prompt": {
        const session = this.#session(params.sessionId);
        const prompt = promptContent(params.prompt);
        const message = this.options.prefixCwd === false ? prompt.text : `[Working directory: ${session.cwd}]\n\n${prompt.text}`;
        const started = await this.client.request("chat.send", { sessionKey: session.sessionKey, message, ...(prompt.media.length ? { media: prompt.media } : {}) }) as { runId: string };
        const listener = (payload: unknown) => {
          const value = payload as { runId?: unknown; event?: Record<string, unknown> };
          if (value.runId !== started.runId || !value.event) return;
          if (value.event.type === "text-delta") this.notify("session/update", { sessionId: session.sessionId, update: { sessionUpdate: "agent_message_chunk", content: { type: "text", text: String(value.event.text ?? "") } } });
          if (value.event.type === "tool-call") this.notify("session/update", { sessionId: session.sessionId, update: { sessionUpdate: "tool_call", toolCallId: String(value.event.id ?? randomUUID()), title: String(value.event.name ?? "tool"), kind: "other", status: "pending", rawInput: value.event.arguments } });
          if (value.event.type === "usage") this.notify("session/update", { sessionId: session.sessionId, update: { sessionUpdate: "usage_update", used: Number((value.event.usage as Record<string, unknown> | undefined)?.inputTokens ?? 0) + Number((value.event.usage as Record<string, unknown> | undefined)?.outputTokens ?? 0), size: 0 } });
        };
        this.client.on("agent", listener);
        try {
          const result = await this.client.request("agent.wait", { runId: started.runId }, 10 * 60_000) as { error?: string };
          if (result.error) throw new Error(result.error);
          return { stopReason: "end_turn" };
        } finally {
          this.client.off("agent", listener);
        }
      }
      case "session/cancel": {
        const session = this.#session(params.sessionId);
        await this.client.request("chat.abort", { sessionKey: session.sessionKey });
        return {};
      }
      case "session/close": {
        const session = this.#session(params.sessionId);
        await this.client.request("chat.abort", { sessionKey: session.sessionKey }).catch(() => undefined);
        this.sessions.delete(session.sessionId);
        return {};
      }
      case "session/set_mode":
        return {};
      default:
        throw Object.assign(new Error(`Method not found: ${request.method}`), { code: -32601 });
    }
  }
}

export async function serveAcpGateway(options: { url: string; token: string; sessionKey?: string; prefixCwd?: boolean }): Promise<void> {
  const client = new GatewayClient(options.url, options.token, "0.3.0", ["operator.read", "operator.write", "operator.approvals"]);
  await client.connect();
  const write = (message: unknown) => process.stdout.write(`${JSON.stringify(message)}\n`);
  const bridge = new NoriAcpBridge(client, (method, params) => write({ jsonrpc: "2.0", method, params }), options);
  process.stdin.setEncoding("utf8");
  let buffer = "";
  const inflight = new Set<Promise<void>>();
  const processLine = (line: string) => {
    const operation = (async () => {
      let request: RpcRequest | undefined;
      try {
        request = JSON.parse(line) as RpcRequest;
        if (!request || request.jsonrpc !== "2.0" || typeof request.method !== "string") throw Object.assign(new Error("Invalid JSON-RPC request"), { code: -32600 });
        if (request.method === "$/cancel_request") return;
        const result = await bridge.handle(request);
        if (request.id !== undefined) write({ jsonrpc: "2.0", id: request.id, result });
      } catch (error) {
        if (request?.id !== undefined) write({ jsonrpc: "2.0", id: request.id, error: { code: (error as Error & { code?: number }).code ?? -32603, message: (error as Error).message } });
      }
    })();
    inflight.add(operation);
    void operation.finally(() => inflight.delete(operation));
  };
  for await (const chunk of process.stdin) {
    buffer += chunk;
    if (buffer.length > 16 * 1024 * 1024) throw new Error("ACP input frame exceeds 16 MiB");
    while (buffer.includes("\n")) {
      const index = buffer.indexOf("\n");
      const line = buffer.slice(0, index).trim();
      buffer = buffer.slice(index + 1);
      if (line) processLine(line);
    }
  }
  await Promise.allSettled([...inflight]);
  client.close();
}
